import React from "react";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Calendar, MapPin, CloudSun, Target, Users2, Printer, BookMarked, Search, Dumbbell } from "lucide-react";
import { SortablePeriod } from "@/components/practice/SortablePeriod";
import { DraggablePlay, DraggableDrill } from "@/components/practice/LibraryItems";

export function PracticeDetailsForm({ p, update }) {
  return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 border-b border-surface-3">
            <div className="md:col-span-2">
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Title</div>
              <Input value={p.title} onChange={(e)=>update({title:e.target.value})}
                className="mt-1 bg-background border-surface-3 rounded-none" data-testid="practice-title" />
            </div>
            <div>
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/>Date</div>
              <Input type="date" value={p.date} onChange={(e)=>update({date:e.target.value})}
                className="mt-1 bg-background border-surface-3 rounded-none" data-testid="practice-date" />
            </div>
            <div>
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Start</div>
              <Input type="time" value={p.start_time} onChange={(e)=>update({start_time:e.target.value})}
                className="mt-1 bg-background border-surface-3 rounded-none" />
            </div>
            <div>
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1"><Users2 className="w-3 h-3"/>Attendance</div>
              <Input type="number" value={p.attendance_expected} onChange={(e)=>update({attendance_expected:+e.target.value||0})}
                className="mt-1 bg-background border-surface-3 rounded-none" />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1"><MapPin className="w-3 h-3"/>Location</div>
              <Input value={p.location} onChange={(e)=>update({location:e.target.value})}
                className="mt-1 bg-background border-surface-3 rounded-none" />
            </div>
            <div className="md:col-span-2">
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1"><CloudSun className="w-3 h-3"/>Weather</div>
              <Input value={p.weather} onChange={(e)=>update({weather:e.target.value})}
                className="mt-1 bg-background border-surface-3 rounded-none" />
            </div>
            <div className="md:col-span-5">
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1"><Target className="w-3 h-3"/>Objectives</div>
              <Textarea value={p.objectives} onChange={(e)=>update({objectives:e.target.value})} rows={2}
                className="mt-1 bg-background border-surface-3 rounded-none" data-testid="practice-objectives" />
            </div>
          </div>
  );
}

export function PeriodTimeline({ p, plays, drills, totalMinutes, addPeriod, setPeriod, removePeriod }) {
  return (
          <div className="p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-condensed uppercase tracking-widest text-sm text-white">Timeline</div>
                <div className="text-[10px] font-condensed uppercase tracking-widest text-amber mt-0.5">
                  {p.periods?.length || 0} periods · {totalMinutes} min · drag {"↕"} to reorder, drop plays here
                </div>
              </div>
              <Button onClick={addPeriod} data-testid="add-period" className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
                <Plus className="w-3 h-3 mr-1"/> Add Period
              </Button>
            </div>

            <div className="grid grid-cols-[auto_60px_2fr_1.5fr_1.5fr_2fr_auto] gap-3 py-2 border-b border-surface-3 text-[10px] font-condensed uppercase tracking-widest text-slate-500">
              <div></div><div className="text-center">Min</div><div>Period</div><div>Coach</div><div>Equipment</div><div>Notes</div><div></div>
            </div>

            <SortableContext
              items={(p.periods || []).map(pd => `period:${pd.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {(p.periods || []).map((pd, i) => (
                <SortablePeriod
                  key={pd.id}
                  p={pd}
                  idx={i}
                  plays={plays}
                  drills={drills}
                  onChange={(np) => setPeriod(i, np)}
                  onRemove={() => removePeriod(i)}
                />
              ))}
            </SortableContext>

            {(!p.periods || p.periods.length === 0) && (
              <div className="text-sm text-slate-500 py-8 text-center">No periods yet — add the first one.</div>
            )}
          </div>
  );
}

export function EditorFooter({ p, del, print, save, saving }) {
  return (
          <div className="border-t border-surface-3 p-4 flex items-center justify-between">
            <div>
              {p.id && (
                <Button variant="ghost" onClick={del} className="rounded-none hover:text-red-400 h-9 text-[11px] font-condensed uppercase tracking-widest">
                  <Trash2 className="w-3 h-3 mr-1"/> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={print} className="rounded-none border-surface-3 hover:border-amber h-9 text-[11px] font-condensed uppercase tracking-widest">
                <Printer className="w-3 h-3 mr-1"/> Print Card
              </Button>
              <Button onClick={save} disabled={saving} data-testid="save-practice" className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-9 text-[11px] font-condensed uppercase tracking-widest">
                <Save className="w-3 h-3 mr-1"/> {saving ? "Saving…" : "Save Practice"}
              </Button>
            </div>
          </div>
  );
}

export function LibraryTabs({ libTab, setLibTab, plays, drills, playSearch, setPlaySearch }) {
  return (
    <>
          <div className="flex border-b border-surface-3">
            <button
              onClick={() => setLibTab("plays")}
              data-testid="lib-tab-plays"
              className={`flex-1 text-[11px] font-condensed uppercase tracking-widest px-3 py-2 flex items-center justify-center gap-1.5 transition-colors ${
                libTab === "plays" ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"
              }`}
            >
              <BookMarked className="w-3 h-3" /> Plays ({plays.length})
            </button>
            <button
              onClick={() => setLibTab("drills")}
              data-testid="lib-tab-drills"
              className={`flex-1 text-[11px] font-condensed uppercase tracking-widest px-3 py-2 flex items-center justify-center gap-1.5 transition-colors ${
                libTab === "drills" ? "bg-amber text-[#0B0D12]" : "text-slate-400 hover:text-white"
              }`}
            >
              <Dumbbell className="w-3 h-3" /> Drills ({drills.length})
            </button>
          </div>

          <div className="p-3 border-b border-surface-3">
            <div className="relative">
              <Search className="w-3 h-3 text-slate-500 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={playSearch} onChange={(e)=>setPlaySearch(e.target.value)}
                placeholder={libTab === "plays" ? "Search plays…" : "Search drills…"}
                className="bg-background border-surface-3 rounded-none pl-7 h-8 text-xs" data-testid="lib-search" />
            </div>
          </div>
    </>
  );
}

export function LibraryList({ libTab, plays, drills, playsBySide, drillsByCat }) {
  return (
          <div className="p-3 space-y-4 max-h-[600px] overflow-y-auto">
            {libTab === "plays" && (
              <>
                {Object.entries(playsBySide).map(([side, list]) => (
                  list.length > 0 && (
                    <div key={side}>
                      <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500 mb-2">
                        {side.replace("_", " ")}
                      </div>
                      <div className="space-y-1.5">
                        {list.map(pl => <DraggablePlay key={pl.id} play={pl} />)}
                      </div>
                    </div>
                  )
                ))}
                {plays.length === 0 && (
                  <div className="text-xs text-slate-500 italic text-center py-6">
                    No plays yet. Install some in the Playbook module.
                  </div>
                )}
              </>
            )}
            {libTab === "drills" && (
              <>
                {Object.entries(drillsByCat).sort().map(([cat, list]) => (
                  <div key={cat}>
                    <div className="text-[10px] font-condensed uppercase tracking-[0.28em] text-slate-500 mb-2">
                      {cat.replace("_", " ")}
                    </div>
                    <div className="space-y-1.5">
                      {list.map(dr => <DraggableDrill key={dr.id} drill={dr} />)}
                    </div>
                  </div>
                ))}
                {drills.length === 0 && (
                  <div className="text-xs text-slate-500 italic text-center py-6">
                    No drills yet. Add some in the Drill Library.
                  </div>
                )}
              </>
            )}
          </div>
  );
}

export function PracticeLibrary(props) {
  return (
        <aside className="bg-surface border border-surface-3" data-testid="play-library">
          <div className="px-4 py-3 border-b border-surface-3">
            <div className="flex items-center gap-2">
              <BookMarked className="w-4 h-4 text-amber" />
              <div className="font-condensed uppercase tracking-[0.25em] text-xs">Practice Library</div>
            </div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 mt-1">Drag onto any period</div>
          </div>
      <LibraryTabs {...props} />
      <LibraryList {...props} />
    </aside>
  );
}
